import { GrowthBook } from '@growthbook/growthbook';
import { autoAttributesPlugin, growthbookTrackingPlugin } from '@growthbook/growthbook/plugins';
import { useLocale } from '@core/base/composables/locale';
import { toFeatureFlagProps } from '../utils/featureFlagEvent';

export default defineNuxtPlugin({
  name: 'growthbook',
  dependsOn: ['uuid'],

  async setup() {
    const { growthbookEnabled } = useAppConfig();
    if (!growthbookEnabled) return { provide: { growthbook: null as unknown as GrowthBook } };

    const store = useKegelAnalyticStore();
    const { public: { GROWTHBOOK_CLIENT_KEY, GROWTHBOOK_API_HOST, ENVIRONMENT } } = useRuntimeConfig();

    if (!GROWTHBOOK_CLIENT_KEY) return { provide: { growthbook: null as unknown as GrowthBook } };

    const { mixpanelTrack, setUserProperty } = useMixpanel();
    const { sendExperiment } = useExperimentApi();
    const nuxtApp = useNuxtApp();
    const locale = useLocale() ?? '';
    const cookieCountry = useCookie('user-country').value ?? '';
    const country = cookieCountry || await $fetch('/api/geo')
      .then((r: any) => r.country as string)
      .catch(() => '');

    const gb = new GrowthBook({
      apiHost: GROWTHBOOK_API_HOST,
      clientKey: GROWTHBOOK_CLIENT_KEY,
      enableDevMode: ENVIRONMENT !== 'production',
      plugins: [
        autoAttributesPlugin({ uuidKey: 'user_id', uuid: store.userUuid, uuidAutoPersist: false }),
        growthbookTrackingPlugin(),
      ],
      attributes: {
        user_id: store.userUuid,
        cmpId: store.cmpId,
        environment: ENVIRONMENT,
        country,
        locale,
      },
      trackingCallback(experiment, result) {
        if (store.experiments.some(e => e.experimentId === experiment.key && e.variation === result.key)) return;

        const variationName = result.name ?? result.key;

        store.experiments = [
          ...store.experiments.filter(e => e.experimentId !== experiment.key),
          { experimentId: experiment.key, variation: result.key },
        ];
        store.currentExperimentKey = experiment.key;
        store.currentExperimentVariation = result.key;

        mixpanelTrack('$experiment_started', {
          Experiment: experiment.key,
          Feature: result.featureId,
          Variation: variationName,
        });
        setUserProperty({
          [`Experiment: ${experiment.key}`]: variationName,
        });
        void sendExperiment(experiment.key, result.key).catch((error: unknown) => {
          nuxtApp.$logger.note({
            category: 'api',
            message: 'sending the experiment failed',
            level: 'warning',
            cause: error,
          });
        });
      },
      onFeatureUsage(key, result) {
        const props = toFeatureFlagProps(key, result, store.featureFlags);
        if (!props) return;

        store.featureFlags = { ...store.featureFlags, [key]: props.Variation };
        mixpanelTrack('Feature Flag Applied', props);
      },
    });

    try {
      await gb.init({ streaming: false, timeout: 3000 });
    } catch { /* gb fail -> fallback */
    }

    return {
      provide: {
        growthbook: gb,
      },
    };
  },
});
